import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Umbrella,
  Stethoscope,
  Heart,
  Baby,
  Users,
  Ban,
  HelpCircle,
  CheckCircle,
  XCircle,
  Activity,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  GET_LEAVE_TYPE_STATISTICS,
  CREATE_LEAVE_TYPE_MUTATION,
  UPDATE_LEAVE_TYPE_MUTATION,
  DELETE_LEAVE_TYPE_MUTATION,
} from '../../graphql/queries';

interface LeaveType {
  id: string;
  code?: string;
  shortCode: string;
  value: string;
  label: string;
  displayName: string;
  color: string;
  description: string;
  requiresApproval: boolean;
  countsAgainstLimit: boolean;
  isActive: boolean;
  sortOrder?: number;
  usageCount?: number;
}

interface LeaveTypeFormData {
  shortCode: string;
  displayName: string;
  color: string;
  description: string;
  requiresApproval: boolean;
  countsAgainstLimit: boolean;
  isActive: boolean;
  sortOrder: number;
}

const EMPTY_FORM: LeaveTypeFormData = {
  shortCode: '',
  displayName: '',
  color: '#168dc8',
  description: '',
  requiresApproval: true,
  countsAgainstLimit: false,
  isActive: true,
  sortOrder: 0,
};

const getLeaveTypeIcon = (type: string) => {
  switch (type) {
    case 'VACATION': return <Umbrella className="w-5 h-5" />;
    case 'SICK_LEAVE': return <Stethoscope className="w-5 h-5" />;
    case 'PERSONAL': return <Heart className="w-5 h-5" />;
    case 'MATERNITY': return <Baby className="w-5 h-5" />;
    case 'PATERNITY': return <Users className="w-5 h-5" />;
    case 'UNPAID': return <Ban className="w-5 h-5" />;
    case 'OTHER': return <HelpCircle className="w-5 h-5" />;
    default: return <HelpCircle className="w-5 h-5" />;
  }
};

const generateShortCode = (displayName: string): string => {
  return displayName
    .split(/\s+/)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
};

export const LeaveTypeManagement: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_LEAVE_TYPE_STATISTICS, {
    fetchPolicy: 'network-only',
  });

  const [createLeaveType] = useMutation(CREATE_LEAVE_TYPE_MUTATION);
  const [updateLeaveType] = useMutation(UPDATE_LEAVE_TYPE_MUTATION);
  const [deleteLeaveType] = useMutation(DELETE_LEAVE_TYPE_MUTATION);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<LeaveType | null>(null);
  const [formData, setFormData] = useState<LeaveTypeFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingType, setDeletingType] = useState<LeaveType | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreateDialog = () => {
    setEditingType(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (type: LeaveType) => {
    setEditingType(type);
    setFormData({
      shortCode: type.shortCode || '',
      displayName: type.displayName,
      color: type.color || '#168dc8',
      description: type.description || '',
      requiresApproval: type.requiresApproval,
      countsAgainstLimit: type.countsAgainstLimit,
      isActive: type.isActive,
      sortOrder: type.sortOrder || 0,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (type: LeaveType) => {
    setDeletingType(type);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.shortCode.trim() || !formData.displayName.trim()) {
      toast.error('Skrót i nazwa wyświetlana są wymagane');
      return;
    }

    setSaving(true);
    try {
      if (editingType) {
        // Update
        await updateLeaveType({
          variables: {
            id: editingType.id,
            shortCode: formData.shortCode.toUpperCase(),
            displayName: formData.displayName,
            color: formData.color,
            description: formData.description,
            requiresApproval: formData.requiresApproval,
            countsAgainstLimit: formData.countsAgainstLimit,
            isActive: formData.isActive,
            sortOrder: formData.sortOrder,
          },
        });
        toast.success(`Typ nieobecności "${formData.displayName}" został zaktualizowany`);
      } else {
        // Create
        await createLeaveType({
          variables: {
            shortCode: formData.shortCode.toUpperCase(),
            displayName: formData.displayName,
            color: formData.color,
            description: formData.description,
            requiresApproval: formData.requiresApproval,
            countsAgainstLimit: formData.countsAgainstLimit,
            isActive: formData.isActive,
            sortOrder: formData.sortOrder,
          },
        });
        toast.success(`Typ nieobecności "${formData.displayName}" został utworzony`);
      }
      setDialogOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Wystąpił błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    setDeleting(true);
    try {
      await deleteLeaveType({ variables: { id: deletingType.id } });
      toast.success(`Typ nieobecności "${deletingType.displayName}" został usunięty`);
      setDeleteDialogOpen(false);
      setDeletingType(null);
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Wystąpił błąd podczas usuwania');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (type: LeaveType) => {
    try {
      await updateLeaveType({
        variables: {
          id: type.id,
          isActive: !type.isActive,
        },
      });
      toast.success(
        type.isActive
          ? `Typ "${type.displayName}" został dezaktywowany`
          : `Typ "${type.displayName}" został aktywowany`
      );
      refetch();
    } catch (err: any) {
      toast.error(err.message || 'Wystąpił błąd');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">Ładowanie typów nieobecności...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-500">Błąd podczas ładowania: {error.message}</div>
      </div>
    );
  }

  const leaveTypes: LeaveType[] = data?.leaveTypeStatistics || [];

  // Calculate statistics
  const totalUsage = leaveTypes.reduce((sum: number, type: LeaveType) => sum + (type.usageCount || 0), 0);
  const activeTypes = leaveTypes.filter((type: LeaveType) => type.isActive).length;
  const mostUsedType = leaveTypes.reduce((max: LeaveType | null, type: LeaveType) =>
    !max || (type.usageCount || 0) > (max.usageCount || 0) ? type : max, null
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Zarządzanie typami nieobecności</h2>
          <p className="text-muted-foreground">Konfiguracja rodzajów nieobecności pracowników</p>
        </div>
        <Button onClick={openCreateDialog} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Dodaj typ nieobecności</span>
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-950/40 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Wszystkie typy</p>
                <p className="text-2xl font-bold text-foreground">{leaveTypes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 dark:bg-green-950/40 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Aktywne typy</p>
                <p className="text-2xl font-bold text-foreground">{activeTypes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 dark:bg-purple-950/40 rounded-lg">
                <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Użyć w tym roku</p>
                <p className="text-2xl font-bold text-foreground">{totalUsage}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-orange-100 dark:bg-orange-950/40 rounded-lg">
                {mostUsedType && getLeaveTypeIcon(mostUsedType.value)}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Najczęściej używany</p>
                <p className="text-lg font-bold text-foreground">{mostUsedType?.displayName || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Types Table */}
      <Card>
        <CardHeader>
          <CardTitle>Typy nieobecności</CardTitle>
          <CardDescription>
            Konfiguracja dostępnych rodzajów nieobecności w systemie
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Typ nieobecności</TableHead>
                <TableHead>Skrót</TableHead>
                <TableHead>Opis</TableHead>
                <TableHead className="text-center">Wymaga zatwierdzenia</TableHead>
                <TableHead className="text-center">Odlicza od limitu</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Użyć w tym roku</TableHead>
                <TableHead>Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaveTypes.map((type: LeaveType) => (
                <TableRow key={type.id || type.value}>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${type.color}20` }}
                      >
                        {getLeaveTypeIcon(type.code || type.value)}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{type.displayName}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {type.shortCode}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{type.description}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    {type.requiresApproval ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <XCircle className="w-5 h-5 text-muted-foreground mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {type.countsAgainstLimit ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <XCircle className="w-5 h-5 text-muted-foreground mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={type.isActive ? "default" : "secondary"}
                      className={
                        type.isActive
                          ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400 cursor-pointer"
                          : "cursor-pointer"
                      }
                      onClick={() => handleToggleActive(type)}
                    >
                      {type.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline">
                      {type.usageCount || 0}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(type)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                        onClick={() => openDeleteDialog(type)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Edytuj typ nieobecności' : 'Dodaj nowy typ nieobecności'}
            </DialogTitle>
            <DialogDescription>
              {editingType
                ? 'Zmień ustawienia wybranego typu nieobecności.'
                : 'Wypełnij dane nowego typu nieobecności.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="shortCode">
                Skrót <span className="text-red-500">*</span>
              </Label>
              <Input
                id="shortCode"
                placeholder="np. UW"
                value={formData.shortCode}
                onChange={(e) =>
                  setFormData({ ...formData, shortCode: e.target.value.toUpperCase() })
                }
                className="font-mono w-24"
                maxLength={5}
                required
              />
              <p className="text-xs text-muted-foreground">
                Krótki unikalny skrót (np. UW = Urlop Wypoczynkowy). Używany w raportach.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">
                Nazwa wyświetlana <span className="text-red-500">*</span>
              </Label>
              <Input
                id="displayName"
                placeholder="np. Szkolenie"
                value={formData.displayName}
                onChange={(e) => {
                  const newDisplayName = e.target.value;
                  const updates: Partial<LeaveTypeFormData> = { displayName: newDisplayName };
                  if (!editingType) {
                    updates.shortCode = generateShortCode(newDisplayName);
                  }
                  setFormData({ ...formData, ...updates });
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Kolor</Label>
              <div className="flex items-center space-x-3">
                <input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-input cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="font-mono w-32"
                  placeholder="#168dc8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Opis</Label>
              <Input
                id="description"
                placeholder="Krótki opis typu nieobecności..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  id="requiresApproval"
                  type="checkbox"
                  checked={formData.requiresApproval}
                  onChange={(e) =>
                    setFormData({ ...formData, requiresApproval: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="requiresApproval" className="cursor-pointer">
                  Wymaga zatwierdzenia
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="countsAgainstLimit"
                  type="checkbox"
                  checked={formData.countsAgainstLimit}
                  onChange={(e) =>
                    setFormData({ ...formData, countsAgainstLimit: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="countsAgainstLimit" className="cursor-pointer">
                  Odlicza od limitu
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="isActive" className="cursor-pointer">
                  Aktywny
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">Kolejność</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
                }
                min={0}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Anuluj
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? 'Zapisywanie...'
                : editingType
                  ? 'Zapisz zmiany'
                  : 'Utwórz typ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Potwierdź usunięcie</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć typ nieobecności "{deletingType?.displayName}"?
              Ta operacja jest nieodwracalna.
            </DialogDescription>
          </DialogHeader>
          {deletingType && (deletingType.usageCount || 0) > 0 && (
            <div className="p-3 text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/20 dark:text-amber-400 rounded-md">
              Ten typ nieobecności został użyty {deletingType.usageCount} razy. Usunięcie może wpłynąć na
              istniejące dane.
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingType(null);
              }}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
